const MEASUREMENT_ID = 'G-5FH6C0R9QT'
const SCRIPT_ID = 'mojopayr-ga4'

const TRACKED_EVENTS = new Set([
    'signup',
    'login',
    'wallet_funded',
    'money_transfer',
    'bill_payment',
    'mobile_topup',
    'merchant_payment',
    'invoice_payment',
    'ticket_purchase',
    'business_registration',
    'community_contribution',
    'community_withdrawal',
    'qr_payment',
    'bank_transfer',
    'contact_form_submit',
])

const ALLOWED_EVENT_PARAMETERS = new Set([
    'account_type',
    'channel',
    'country_code',
    'currency',
    'method',
    'payment_type',
    'provider_category',
    'service_type',
    'source',
    'status',
])

const isProduction = () => process.env.NODE_ENV === 'production'

export const respectsDoNotTrack = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false

    const setting = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack
    return setting === '1' || setting === 'yes'
}

export const canUseAnalytics = () => (
    isProduction()
    && typeof window !== 'undefined'
    && !respectsDoNotTrack()
    && window[`ga-disable-${MEASUREMENT_ID}`] !== true
)

const cleanParameters = (parameters = {}) => Object.fromEntries(
    Object.entries(parameters)
        .filter(([key, value]) => ALLOWED_EVENT_PARAMETERS.has(key) && value != null)
        .map(([key, value]) => [key, String(value).slice(0, 100)])
)

export const initializeGoogleAnalytics = () => {
    if (!canUseAnalytics()) return false
    if (document.getElementById(SCRIPT_ID)) return true

    window.dataLayer = window.dataLayer || []
    window.gtag = window.gtag || function gtag() {
        window.dataLayer.push(arguments)
    }

    window.gtag('js', new Date())
    window.gtag('config', MEASUREMENT_ID, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
    })

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
    document.head.appendChild(script)

    return true
}

export const trackPageView = (pathname, title = document.title) => {
    if (!canUseAnalytics() || typeof window.gtag !== 'function') return false

    const safePath = typeof pathname === 'string' && pathname.startsWith('/') ? pathname : '/'
    window.gtag('event', 'page_view', {
        page_title: title,
        page_location: `${window.location.origin}${safePath}`,
        page_path: safePath,
        send_to: MEASUREMENT_ID,
    })
    return true
}

export const trackEvent = (eventName, parameters = {}) => {
    if (!TRACKED_EVENTS.has(eventName)) return false
    if (!canUseAnalytics() || typeof window.gtag !== 'function') return false

    window.gtag('event', eventName, {
        ...cleanParameters(parameters),
        send_to: MEASUREMENT_ID,
    })
    return true
}

export const analyticsEvents = {
    signup: (parameters) => trackEvent('signup', parameters),
    login: (parameters) => trackEvent('login', parameters),
    walletFunded: (parameters) => trackEvent('wallet_funded', parameters),
    moneyTransfer: (parameters) => trackEvent('money_transfer', parameters),
    billPayment: (parameters) => trackEvent('bill_payment', parameters),
    mobileTopup: (parameters) => trackEvent('mobile_topup', parameters),
    merchantPayment: (parameters) => trackEvent('merchant_payment', parameters),
    invoicePayment: (parameters) => trackEvent('invoice_payment', parameters),
    ticketPurchase: (parameters) => trackEvent('ticket_purchase', parameters),
    businessRegistration: (parameters) => trackEvent('business_registration', parameters),
    communityContribution: (parameters) => trackEvent('community_contribution', parameters),
    communityWithdrawal: (parameters) => trackEvent('community_withdrawal', parameters),
    qrPayment: (parameters) => trackEvent('qr_payment', parameters),
    bankTransfer: (parameters) => trackEvent('bank_transfer', parameters),
    contactFormSubmit: (parameters) => trackEvent('contact_form_submit', parameters),
}

export { MEASUREMENT_ID }
